/**
 * Contractor Ratings API.
 *
 * Internal rating that admin/coordinator/superAdmin assign to the contractor.
 * Backend: `code/backend/src/modules/contractor-ratings/`.
 *
 * IMPORTANT: endpoints protected by RolesGuard — calls with a
 * contractor/client/freelanceEc token return 403. The actions should only be
 * dispatched after checking the role via `isUserRole(...)`.
 */
import { http } from "./client";

/** GET /contractorRatings/:contractorId — fetches a contractor's rating. */
export const fetchContractorRating = (contractorId: string) =>
  http({
    method: "GET",
    url: `contractorRatings/${contractorId}`,
    isToken: true,
  });

/** GET /contractorRatings?contractorIds=id1,id2,... — batch for listings. */
export const fetchContractorRatingsBatch = (contractorIds: string[]) =>
  http({
    method: "GET",
    url: "contractorRatings",
    params: { contractorIds: contractorIds.join(",") },
    isToken: true,
  });

/**
 * PUT /contractorRatings/:contractorId — upserts the score and, when `note`
 * carries text, appends it as a comment in the same request (the modal has one
 * Save button; two requests could land the stars and lose the comment).
 */
export const upsertContractorRating = (
  contractorId: string,
  data: { score: number; note?: string },
) =>
  http({
    method: "PUT",
    url: `contractorRatings/${contractorId}`,
    data,
    isToken: true,
  });

/** POST /contractorRatings/:contractorId/notes — adds a comment, keeps the score. */
export const addContractorRatingNote = (contractorId: string, text: string) =>
  http({
    method: "POST",
    url: `contractorRatings/${contractorId}/notes`,
    data: { text },
    isToken: true,
  });

/**
 * DELETE /contractorRatings/:contractorId/notes/:noteId — author or superAdmin
 * (the backend 403s anyone else).
 */
export const deleteContractorRatingNote = (
  contractorId: string,
  noteId: string,
) =>
  http({
    method: "DELETE",
    url: `contractorRatings/${contractorId}/notes/${noteId}`,
    isToken: true,
  });

/** DELETE /contractorRatings/:contractorId — clears the rating. */
export const deleteContractorRating = (contractorId: string) =>
  http({
    method: "DELETE",
    url: `contractorRatings/${contractorId}`,
    isToken: true,
  });
